/* eslint-disable @typescript-eslint/no-invalid-void-type */
import type { ApiFnType } from '../../api-client';
import { noop } from '../../utils/func-utils';

interface ApiActionType<F extends ApiFnType<unknown>> {
  (...args: Parameters<F>): Promise<Nullable<ReturnType<F>> | Error>;
}

interface ApiActionResponders<F extends ApiFnType<unknown>> {
  onCall: (...params: Parameters<F>) => void;
  onDone: (
    resp: Nullable<ReturnType<F>>,
    modified: boolean,
    etag: string
  ) => Nullable<ReturnType<F>> | void;
  onFailure: (Error) => Error | void;
}

type BaseStore = {
  loading: { [key: string]: boolean };
  failure: { [key: string]: Error };
};

const isBaseBriefRoute = (url: string, state: BaseStore): boolean => {
  if (!url) return false;
  if (url.startsWith('/api/brief')) return false;

  const briefId =
    'brief' in state && 'id' in (state as { brief: object }).brief
      ? (state as { brief: { id: number } })?.brief?.id
      : null;

  return url.startsWith(`/api/brief/${briefId}`);
};

function handleApiLifecycle<F extends ApiFnType<unknown>>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set: any,
  getKey: string | (() => string),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ready: any = noop,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  preview: any = noop
): ApiActionResponders<F> {
  const keyFn = typeof getKey === 'function' ? getKey : () => getKey;

  // let reverseChanges = [];
  let key;
  return {
    onCall: () => {
      key = keyFn();
      set(
        (state) => {
          delete state.failure[key];
          state.loading[key] = true;
          // Optimistic update
          // FIXME get immer working with the zustand lenses
          // preview(state, ...args);
        }
        // FIXME get immer working with the zustand lenses
        // (_ignored, reversePatches) => {
        //   reverseChanges = reversePatches;
        // }
      );
    },
    onDone: (data, modified) => {
      set((state) => {
        state.loading[key] = false;
        ready(state, data, modified);
      });
      return data;
    },
    onFailure: (e) => {
      set((state) => {
        if (e.config.url && isBaseBriefRoute(e.config.url, state)) return;
        // applyPatches(state, reverseChanges);
        state.failure[key] = e;
        state.loading[key] = false;
      });
    },
  };
}

function apiAction<F extends ApiFnType<unknown>>(
  apiFn: F,
  { onCall = noop, onDone = noop, onFailure = noop }: ApiActionResponders<F>
): (...args: Parameters<F>) => Promise<Awaited<ReturnType<F>>['data']> {
  const actionNonce = {};
  let etag;
  // eslint-disable-next-line consistent-return
  return async (...args: Parameters<F>): Promise<Awaited<ReturnType<F>>['data']> => {
    const payloadId = args[0]?.id ?? 'unknown';
    // eslint-disable-next-line no-multi-assign
    const nonce = (actionNonce[payloadId] = {});
    try {
      onCall(...args);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = await apiFn(...(args as any));

      // For most update* actions, the object being updated has a known id
      // In those cases, we can reject out of order responses
      if (payloadId === 'unknown' || nonce === actionNonce[payloadId]) {
        delete actionNonce[payloadId];
        if (resp) {
          const { headers = {} } = resp;
          const modified = headers.etag !== etag;
          ({ etag } = headers);
          return await onDone(resp.status === 404 ? null : resp.data, modified, etag);
        }
        return await onDone(null, true, '');
      }
    } catch (e) {
      if (nonce === actionNonce[payloadId]) {
        delete actionNonce[payloadId];
        return onFailure(e);
      }
    }
  };
}

// Selectors
function getApiStatus(key: string) {
  return ({
    loading: { [key]: loading },
    failure: { [key]: failure },
  }: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any): [loading: any, failure: any] => [loading, failure];
}

export default apiAction;
export type { ApiActionType };
export { handleApiLifecycle, getApiStatus };
